import { useCallback, useEffect, useState } from "react";
import { FlaskConical, ShieldAlert, ShieldCheck } from "lucide-react";
import { armExecRole, getEnv, getExecRole } from "@/lib/api";

// App-wide, unmissable indicator of whether this environment trades REAL money
// (mainnet) or PLAY money (testnet), plus the LIVE execution-role state.
// The role is armed with one click from the PRODUCTION dashboard: the backend
// self-verifies (nonce probe through the public domain) that it is the pod
// really serving that host, so a forked preview can never go live.
export const EnvBanner = () => {
  const [env, setEnv] = useState(null);
  const [role, setRole] = useState(null);
  const [arming, setArming] = useState(false);
  const [armMsg, setArmMsg] = useState(null);

  const loadRole = useCallback(() => {
    getExecRole().then(setRole).catch(() => {});
  }, []);

  useEffect(() => {
    getEnv().then(setEnv).catch(() => {});
    loadRole();
    const t = setInterval(loadRole, 60000);
    return () => clearInterval(t);
  }, [loadRole]);

  const onArm = async () => {
    setArming(true);
    setArmMsg(null);
    try {
      const res = await armExecRole();
      if (res.ok === false) {
        setArmMsg(res.reason || "arming rifiutato");
      } else if (!res.live) {
        setArmMsg("armato: conferma del pod in corso (max 30s)…");
        setTimeout(loadRole, 5000);
        setTimeout(loadRole, 35000);
      }
      loadRole();
    } catch {
      setArmMsg("errore di rete durante l'arming");
    } finally {
      setArming(false);
    }
  };

  if (!env) return null;

  if (env.isTestnet) {
    return (
      <div
        data-testid="env-banner-testnet"
        className="flex items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-amber-200"
      >
        <FlaskConical className="h-5 w-5 shrink-0 text-amber-300" />
        <p className="text-sm leading-snug">
          <b className="font-semibold uppercase tracking-wide">Ambiente di prova · TESTNET</b>
          <span className="ml-2 opacity-90">
            I dati Hyperliquid qui sono <b>denaro FINTO</b>. I tuoi soldi reali sono solo
            sull'app di <b>produzione</b> (mainnet).
          </span>
        </p>
      </div>
    );
  }

  // Produzione: se il ruolo LIVE non e' attivo, l'app NON esegue nulla di reale.
  if (role && !role.live) {
    return (
      <div
        data-testid="env-banner-role-disarmed"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-red-500/50 bg-red-500/10 px-4 py-2.5 text-red-200"
      >
        <ShieldAlert className="h-5 w-5 shrink-0 text-red-300" />
        <p className="min-w-0 flex-1 text-sm leading-snug">
          <b className="font-semibold uppercase tracking-wide">Esecuzione reale DISARMATA</b>
          <span className="ml-2 opacity-90">
            Copy Telegram, ordini reali e notifiche sono in pausa su questo ambiente.
          </span>
          {armMsg && <span className="ml-2 font-medium text-red-100">{armMsg}</span>}
        </p>
        <button
          data-testid="arm-live-role-btn"
          onClick={onArm}
          disabled={arming}
          className="shrink-0 rounded-md border border-red-400/60 bg-red-500/20 px-4 py-1.5 text-sm font-semibold uppercase tracking-wide text-red-100 transition-colors hover:bg-red-500/40 disabled:opacity-50"
        >
          {arming ? "Armo…" : "Arma LIVE"}
        </button>
      </div>
    );
  }

  if (role && role.live) {
    return (
      <div
        data-testid="env-banner-role-live"
        className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-1.5 text-emerald-300"
      >
        <ShieldCheck className="h-4 w-4 shrink-0" />
        <p className="text-xs leading-snug opacity-90">
          Ruolo <b>LIVE</b> attivo su <b>{role.host || "questo dominio"}</b> — esecuzione reale armata.
        </p>
      </div>
    );
  }

  return null;
};
